import { Component } from "react"

import { TelegramLink } from "@/shared/ui/link"

import { getSupportUrl } from "@/shared/config"
import { i18n } from "@/shared/i18n"

import { reportErrorToServer } from "./report-error"
import { ErrorBoundaryContext } from "./use-error-boundary"

import type { ErrorBoundaryContextValue } from "./use-error-boundary"

import errorImage from "@/assets/img/webp/sticker.webp"

const STALE_CHUNK_RELOAD_KEY = "celestia:stale-chunk-reloaded"
const STALE_CHUNK_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading (CSS )?chunk/i

/**
 * A deploy swaps hashed asset names under an open session; the next lazy route then fails to
 * import. One automatic reload fetches the new index; the flag keeps a real outage from looping.
 */
const reloadOnceForStaleChunk = (error: Error): boolean => {
  if (!STALE_CHUNK_PATTERN.test(error.message)) return false
  try {
    if (sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) === "1") return false
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, "1")
  } catch {
    return false
  }
  window.location.reload()
  return true
}

const hasResetKeysChanged = (prev: readonly unknown[], next: readonly unknown[]): boolean => {
  if (prev.length !== next.length) return true
  return prev.some((key, index) => !Object.is(key, next[index]))
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?:
    | React.ReactNode
    | ((props: { error: Error; errorInfo: React.ErrorInfo; resetErrorBoundary: () => void }) => React.ReactNode)
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  onReset?: () => void
  resetKeys?: readonly unknown[]
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  prevResetKeys: readonly unknown[]
  resetKeysTriggered: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      prevResetKeys: props.resetKeys ?? [],
      resetKeysTriggered: false
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, errorInfo: null }
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState
  ): Partial<ErrorBoundaryState> | null {
    const nextResetKeys = props.resetKeys ?? []
    const keysChanged = hasResetKeysChanged(state.prevResetKeys, nextResetKeys)

    if (keysChanged && state.hasError) {
      return {
        prevResetKeys: nextResetKeys,
        resetKeysTriggered: true
      }
    }
    if (keysChanged) {
      return { prevResetKeys: nextResetKeys }
    }
    return null
  }

  componentDidUpdate() {
    if (this.state.resetKeysTriggered) {
      try {
        this.props.onReset?.()
      } catch (error: unknown) {
        console.error("[ErrorBoundary] onReset failed:", error)
      }

      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        resetKeysTriggered: false
      })
    }
  }

  resetErrorBoundary = () => {
    try {
      this.props.onReset?.()
    } catch (error: unknown) {
      console.error("[ErrorBoundary] onReset failed:", error)
    }
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  showBoundary = (error: unknown) => {
    const normalizedError = error instanceof Error ? error : new Error(String(error))
    const errorInfo: React.ErrorInfo = { componentStack: "" }

    this.setState({ hasError: true, error: normalizedError, errorInfo })
    this.notifyErrorHandlers(normalizedError, errorInfo)
  }

  handleReload = () => {
    window.location.reload()
  }

  notifyErrorHandlers = (error: Error, errorInfo: React.ErrorInfo) => {
    void reportErrorToServer(error, errorInfo)
    try {
      this.props.onError?.(error, errorInfo)
    } catch (callbackError: unknown) {
      console.error("[ErrorBoundary] onError failed:", callbackError)
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (reloadOnceForStaleChunk(error)) return
    this.setState({ errorInfo })
    this.notifyErrorHandlers(error, errorInfo)
  }

  render() {
    const contextValue: ErrorBoundaryContextValue = {
      showBoundary: this.showBoundary,
      resetBoundary: this.resetErrorBoundary
    }

    let content: React.ReactNode

    if (this.state.hasError && this.state.error) {
      if (typeof this.props.fallback === "function") {
        content = this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo ?? { componentStack: "" },
          resetErrorBoundary: this.resetErrorBoundary
        })
      } else if (this.props.fallback) {
        content = this.props.fallback
      } else {
        content = (
          <div className="space-y-2 p-4 text-center">
            <h2 className="inline-flex items-center justify-center gap-2 text-lg font-semibold text-red-400">
              <svg
                aria-hidden="true"
                focusable="false"
                className="size-[1em]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
              {i18n.t("common:error-boundary-title", "Something went wrong")}
            </h2>

            <pre className="whitespace-pre-wrap break-words text-sm text-yellow-300">{this.state.error.message}</pre>

            <img
              src={errorImage}
              alt="Error"
              className="mx-auto aspect-square w-full max-w-[16rem]"
              height="512"
              width="512"
            />

            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={this.resetErrorBoundary}
                className="rounded-md bg-yellow-600 px-4 py-2 text-sm font-medium text-white hover-device:hover:bg-yellow-500">
                {i18n.t("common:error-boundary-try-again", "Try again")}
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="rounded-md bg-gray-700 px-4 py-2 text-sm font-medium text-white hover-device:hover:bg-gray-600">
                {i18n.t("common:error-boundary-reload", "Reload page")}
              </button>
            </div>
            <TelegramLink
              href={getSupportUrl()}
              className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-semibold text-yellow-400 underline underline-offset-2 transition-all hover-device:hover:text-yellow-300 hover-device:hover:underline">
              <svg
                aria-hidden="true"
                focusable="false"
                className="size-[1em]"
                viewBox="0 0 24 24"
                fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
              </svg>
              {i18n.t("common:error-boundary-contact-support", "Contact support")}
            </TelegramLink>
          </div>
        )
      }
    } else {
      content = this.props.children
    }

    return <ErrorBoundaryContext value={contextValue}>{content}</ErrorBoundaryContext>
  }
}
